import { Autocomplete, TextField } from "@mui/material";
import { FormComponentProps } from "components/Form/components/FormComponentProps";
import { Input } from "components/Form/components/Input/Input";
import { rolesAutocompleteSelector } from "components/RoleSelect/RoleSelect";
import { useMemo } from "react";
import { useSelector } from "react-redux";

/**
 * Normalise the stored value (null or a list of role names) to the list the
 * picker shows. Anything that is not a list of strings shows as empty.
 */
export const toRoleNameList = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];

/**
 * Store an empty selection as null, so "no roles chosen" keeps the default
 * behaviour (the backend treats null and a list differently: an empty list
 * would mean "run with no roles").
 */
export const fromRoleNameList = (names: string[]): string[] | null =>
    names.length > 0 ? names : null;

/**
 * Multi-select of role NAMES (the stored value is a list of names, or null).
 * Options come from the workspace role list the websocket keeps in the store.
 */
export const RoleNamesFormComponent = ({
    input,
    meta,
    fieldProps,
    fieldAttr,
}: FormComponentProps) => {
    const roles = useSelector(rolesAutocompleteSelector);
    const options = useMemo(
        () =>
            Array.from(
                new Set<string>(
                    (roles as Array<{ name?: string }>)
                        .map((role) => role?.name)
                        .filter((name): name is string => !!name)
                )
            ).sort((a, b) => a.localeCompare(b)),
        [roles]
    );
    if (fieldAttr?.hidden) return null;
    const value = toRoleNameList(input.value);
    return (
        <Input fieldProps={fieldProps} fieldAttr={fieldAttr} input={input} meta={meta}>
            <Autocomplete
                multiple
                options={options}
                value={value}
                disabled={fieldAttr?.disabled}
                filterSelectedOptions
                sx={{ width: "400px" }}
                // lift the popper above antd modals (z-index 1500), as RoleSelectAutocomplete does
                slotProps={{ popper: { sx: { zIndex: 2200 } } }}
                onChange={(_event, names: string[]) => input.onChange(fromRoleNameList(names))}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        placeholder={value.length === 0 ? "Default: propagating roles" : ""}
                    />
                )}
            />
        </Input>
    );
};
